<!-- Full-screen Flex Container -->
<div class="flex h-screen w-screen overflow-hidden bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100 relative">

    <!-- MOBILE BACKDROP -->
    <div 
        v-if="isMobileOpen" 
        @click="isMobileOpen = false" 
        class="fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden"
    ></div>

    <!-- LEFT PANEL: Sidebar -->
    <aside 
        :class="[
            'fixed inset-y-0 left-0 z-50 lg:static lg:translate-x-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col h-full text-slate-800 dark:text-slate-300 transition-all duration-300 ease-in-out',
            isMobileOpen ? 'translate-x-0' : '-translate-x-full',
            isCollapsed ? 'lg:w-16' : 'lg:w-64',
            'w-64'
        ]"
    >
        <!-- App Brand Logo & Toggle Button -->
        <div v-show="!isCollapsed || isMobileOpen" class="flex lg:hidden items-center justify-between px-4 py-4 border-b border-slate-200 dark:border-slate-800 min-h-16.25">
            <!-- Logo show when it's phone mode -->
            <div class="flex items-center space-x-3 overflow-hidden">
                <div v-if="formConfigJson.logo" class="flex justify-center items-center h-full">
                    <img
                        :src="formConfigJson.logo"
                        class="h-20 w-auto object-contain"
                    />
                </div>
            </div>
        </div>

        <!-- Left Sidebar Dynamic Menu -->
        <nav class="flex-1 px-2 py-4 space-y-4 overflow-y-auto" @click="isMobileOpen = false">

            <!-- Loading Skeleton -->
            <div v-if="isLoadingMenu" class="space-y-3 px-2">
                <div class="h-4 bg-slate-200 dark:bg-slate-800 rounded w-1/2 animate-pulse"></div>
                <div class="h-8 bg-slate-200 dark:bg-slate-800 rounded animate-pulse"></div>
                <div class="h-8 bg-slate-200 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>

            <!-- Section Level -->
            <div v-else v-for="(section, sectionIdx) in menuList" :key="sectionIdx" class="space-y-1">
                <!-- Section Title Header -->
                <div 
                    v-if="section.label && (!isCollapsed || isMobileOpen)"
                    class="px-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 pt-2 pb-1 truncate"
                >
                    {{ section.label }}
                </div>

                <!-- Section Menu Items -->
                <MenuItem 
                    v-for="(item, itemIdx) in section.items" 
                    :key="itemIdx" 
                    :item="item" 
                    :is-collapsed="isCollapsed && !isMobileOpen"
                />
            </div>

        </nav>

        <!-- FOOTER DRAWER MOBILE (Hanya Tampil di HP: Profile, Theme, & Logout Pindah Ke Sini) -->
        <div class="lg:hidden border-t border-slate-200 dark:border-slate-800 p-4 space-y-4 shrink-0 bg-slate-50/50 dark:bg-slate-900/50">
            <!-- Profile Info -->
            <div class="flex items-center space-x-3">
                <img :src="userAvatar" alt="User Avatar" class="h-10 w-10 rounded-full object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
                <div class="flex flex-col min-w-0">
                    <span class="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {{ userName }}
                    </span>
                    <span class="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {{ userEmail }}
                    </span>
                </div>
            </div>

            <!-- Actions: Theme Switcher & Logout -->
            <div class="flex items-center justify-between gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-800">
                <button 
                    @click="toggleTheme" 
                    type="button"
                    class="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-xs font-semibold cursor-pointer"
                >
                    <i :class="isDark ? 'pi pi-sun text-amber-400' : 'pi pi-moon text-indigo-400'" class="text-base"></i>
                    <span>{{ isDark ? 'Light' : 'Dark' }}</span>
                </button>

                <button 
                    @click="handleLogout" 
                    class="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
                >
                    <i class="pi pi-sign-out text-xs"></i>
                    <span>Logout</span>
                </button>
            </div>
        </div>
    </aside>

    <!-- SESSION EXPIRATION MODAL OVERLAY -->
    <SessionLoginModal 
        :is-open="showSessionModal"
        :user-name="userName"
        @reauthenticated="handleSessionRenewed"
        @logout="handleLogout"
    />

    <!-- RIGHT CONTAINER (Top Header + Main Content) -->
    <div class="flex flex-1 flex-col h-full overflow-hidden w-full">
        <!-- TOP PANEL: Header -->
        <header class="h-16 w-full bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 md:px-6 flex items-center justify-between shrink-0 shadow-sm gap-2 relative">
            
            <!-- LEFT SIDE HEADER (Breadcrumb and Hamburger button on phone) -->
            <div class="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
                <!-- Mobile Hamburger Button -->
                <button 
                    @click="isMobileOpen = !isMobileOpen"
                    class="lg:hidden p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 cursor-pointer mr-1 shrink-0"
                >
                    <i class="pi pi-bars text-base"></i>
                </button>

                <!-- Collapse Toggle Button (Desktop Only) -->
                <button 
                    v-show="!isMobileOpen"
                    @click="toggleSidebar"
                    style="background: blue;"
                    class="hidden lg:flex p-1.5 rounded-lg -ml-7 mr-3 z-100
                        bg-slate-100 dark:bg-slate-800 
                        hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 
                        dark:text-slate-300 transition-colors mx-auto cursor-pointer"
                    :title="isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
                >
                    <i 
                        class="pi transition-transform duration-300 font-bold text-xl"
                        :class="isCollapsed ? 'fa-solid fa-square-up-right' : 'fa-solid fa-circle-chevron-left'"
                    ></i>
                </button>

                <span class="hidden sm:inline">Home</span>
                <span class="hidden sm:inline">/</span>
                <span class="font-semibold text-slate-800 dark:text-slate-200 capitalize truncate">
                    {{ currentRouteName }}
                </span>

                <!-- Session Timer Badge -->
                <div class="font-bold italic text-7xl transition-colors duration-300 z-10"
                    :class="((Number(timeLeft.split(':')[0]) + Number(timeLeft.split(':')[1]) / 60) < 0.30) ? 'text-black/30 dark:text-black/50' : 'text-white/30 dark:text-black/5'"
                >
                    {{ timeLeft }}
                </div>
            </div>

            <!-- LOGO DI TENGAH (Hanya Tampil di Desktop / Layar Besar) -->
            <div class="hidden lg:flex items-center space-x-3 absolute left-1/2 -translate-x-1/2">
                <div v-if="formConfigJson.logo" class="flex justify-center items-center h-full">
                    <img
                        :src="formConfigJson.logo"
                        class="h-15 w-auto object-contain"
                    />
                </div>
            </div>

            <!-- RIGHT HEADER ACTIONS (Theme, User Profile, Logout) -->
            <div class="hidden lg:flex items-center space-x-4 shrink-0">
                <!-- Theme Switcher -->
                <button 
                    @click="toggleTheme" 
                    type="button"
                    class="flex items-center gap-1.5 p-2 md:px-3 md:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer text-xs font-semibold"
                    :title="isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
                >
                    <i :class="isDark ? 'pi pi-sun text-amber-400' : 'pi pi-moon text-indigo-400'" class="text-base md:text-lg"></i>
                    <span class="hidden md:inline">{{ isDark ? 'Light' : 'Dark' }}</span>
                </button>
                                
                <!-- User Profile -->
                <div class="flex items-center space-x-2">
                    <img :src="userAvatar" alt="User Avatar" class="h-8 w-8 md:h-9 md:w-9 rounded-full object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
                    <span class="text-sm font-medium text-slate-700 dark:text-slate-200 hidden sm:inline truncate max-w-25 md:max-w-none">
                        {{ userName }}
                    </span>
                </div>

                <!-- Logout Button -->
                <button @click="handleLogout" class="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors shadow-sm cursor-pointer shrink-0">
                    Logout
                </button>
            </div>
        </header>
    
        <main class="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50 dark:bg-slate-900">            
            <router-view />
        </main>
    </div>

</div>